import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, Modal, InputGroup, Alert, Spinner, Toast, ToastContainer, Tabs, Tab, ListGroup } from 'react-bootstrap';
import { Plus, Edit, Trash2, Key, Search, AlertTriangle, CheckCircle, User, Upload, Download, FileText, File, ChevronDown, ChevronUp, Calendar, Clock } from 'lucide-react';
import { getTokens, createToken, updateToken, deleteToken, getDecryptedToken, toggleScheduledMode } from '../services/tokens';
import { getPersonaByTokenId, createPersona, updatePersona, deletePersona } from '../services/personas';
import { 
  uploadTokensCSV, 
  uploadTokensJSON, 
  uploadTokensTXT, 
  generateCSVTemplate, 
  generateJSONTemplate,
  generateTXTTemplate  
} from '../services/bulkUpload';

const Tokens = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [decryptedTokens, setDecryptedTokens] = useState({});
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    token: '',
    is_active: true,
    usage_count: 0,
    bot_type: 'auto',
    fail_count: 0,
    success_count: 0,
    usage_threshold: 100,
    cooldown_duration: 60,
    client_type: 'web',
    proxy: null,
    emoji: null,
    status: 'healthy',
    label: null,
    last_error_code: null,
    token_hash: null,
    proxy_id: null,
    is_scheduled: false,
    scheduled_days: [],
    scheduled_start_time: '',
    scheduled_end_time: '',
    scheduled_timezone: 'America/New_York',
    response_delay_minutes: 20
  });
  const [submitting, setSubmitting] = useState(false);
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tokenToDelete, setTokenToDelete] = useState(null);
  const [newToken, setNewToken] = useState(null);
  const [showPersonaModal, setShowPersonaModal] = useState(false);
  const [currentPersona, setCurrentPersona] = useState(null);
  const [personaFormData, setPersonaFormData] = useState({
    persona: ''
  });
  const [personas, setPersonas] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('csv');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [schedulingToken, setSchedulingToken] = useState(null);
  const [scheduleFormData, setScheduleFormData] = useState({
    is_scheduled: false,
    scheduled_days: [],
    scheduled_start_time: '09:00',
    scheduled_end_time: '17:00',
    scheduled_timezone: 'America/New_York',
    response_delay_minutes: 20
  });
  const fileInputRef = useRef(null);

  // Days of week options
  const daysOfWeek = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  // Timezone options
  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'UTC', label: 'UTC' }
  ];

  // Fetch tokens from API
  const fetchTokens = async () => {
    try {
      setLoading(true);
      const response = await getTokens();
      setTokens(response);
      
      // Fetch decrypted tokens for display
      const decryptedData = {};
      for (const token of response) {
        try {
          const decryptedResponse = await getDecryptedToken(token.id);
          decryptedData[token.id] = decryptedResponse.decrypted_token;
        } catch (error) {
          console.error('Failed to fetch decrypted token:', token.id, error);
          decryptedData[token.id] = 'N/A';
        }
      }
      setDecryptedTokens(decryptedData);
      
      // Fetch personas for custom tokens
      const customTokens = response.filter(token => token.bot_type === 'custom');
      const personasData = {};
      
      for (const token of customTokens) {
        try {
          const persona = await getPersonaByTokenId(token.id);
          personasData[token.id] = persona;
        } catch (error) {
          console.error('Failed to fetch persona for token:', token.id, error);
          personasData[token.id] = null;
        }
      }
      
      setPersonas(personasData);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      showToast('Failed to load tokens: ' + (error.message || error), 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  // Show toast notification
  const showToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setToastShow(true);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'cooldown': return 'warning';
      case 'banned': return 'danger';
      default: return 'secondary';
    }
  };

  const getClientTypeBadge = (clientType) => {
    switch (clientType) {
      case 'web': return 'primary';
      case 'desktop': return 'info';
      case 'mobile': return 'success';
      default: return 'secondary';
    }
  };

  const getBotTypeBadge = (botType) => {
    switch (botType) {
      case 'auto': return 'success';
      case 'custom': return 'primary';
      default: return 'secondary';
    }
  };

  const filteredTokens = tokens.filter(token => {
    const matchesSearch = (token.label && token.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (token.token && token.token.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'active' && token.is_active) ||
                         (filterStatus === 'inactive' && !token.is_active);
    return matchesSearch && matchesFilter;
  });

  const toggleRowExpansion = (tokenId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(tokenId)) {
      newExpandedRows.delete(tokenId);
    } else {
      newExpandedRows.add(tokenId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleEdit = (token) => {
    setEditingToken(token);
    setFormData({
      token: token.token || '',
      is_active: token.is_active,
      usage_count: token.usage_count || 0,
      bot_type: token.bot_type || 'auto',
      fail_count: token.fail_count || 0,
      success_count: token.success_count || 0,
      usage_threshold: token.usage_threshold || 100,
      cooldown_duration: token.cooldown_duration || 60,
      client_type: token.client_type || 'web',
      proxy: token.proxy || null,
      emoji: token.emoji || null,
      status: token.status || 'healthy',
      label: token.label || null,
      last_error_code: token.last_error_code || null,
      token_hash: token.token_hash || null,
      proxy_id: token.proxy_id || null,
      is_scheduled: token.is_scheduled || false,
      scheduled_days: token.scheduled_days || [],
      scheduled_start_time: token.scheduled_start_time || '09:00',
      scheduled_end_time: token.scheduled_end_time || '17:00',
      scheduled_timezone: token.scheduled_timezone || 'America/New_York',
      response_delay_minutes: token.response_delay_minutes || 20
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingToken(null);
    setFormData({
      token: '',
      is_active: true,
      usage_count: 0,
      bot_type: 'auto',
      fail_count: 0,
      success_count: 0,
      usage_threshold: 100,
      cooldown_duration: 60,
      client_type: 'web',
      proxy: null,
      emoji: null,
      status: 'healthy',
      label: null,
      last_error_code: null,
      token_hash: null,
      proxy_id: null,
      is_scheduled: false,
      scheduled_days: [],
      scheduled_start_time: '09:00',
      scheduled_end_time: '17:00',
      scheduled_timezone: 'America/New_York',
      response_delay_minutes: 20
    });
    setShowModal(true);
  };

  const handleDeleteClick = (token) => {
    setTokenToDelete(token);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (tokenToDelete) {
      try {
        await deleteToken(tokenToDelete.id);
        fetchTokens();
        showToast('Token deleted successfully');
      } catch (error) {
        console.error('Failed to delete token:', error);
        showToast('Failed to delete token: ' + (error.message || error), 'danger');
      } finally {
        setShowDeleteModal(false);
        setTokenToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTokenToDelete(null);
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    console.log('Submitting token data:', formData); // Debug log
    
    // Clean the data before sending
    const cleanData = { ...formData };
    delete cleanData.encrypted_data;
    delete cleanData.token_hash;
    
    // Don't send token if it's not changed (for updates)
    if (editingToken && cleanData.token === decryptedTokens[editingToken.id]) {
      delete cleanData.token;
    }
    
    console.log('Cleaned data to send:', cleanData); // Debug log
    
    if (editingToken) {
      await updateToken(editingToken.id, cleanData);
      showToast('Token updated successfully');
    } else {
      await createToken(cleanData);
      showToast('Token created successfully');
    }
    setShowModal(false);
    fetchTokens();
  } catch (error) {
    console.error('Failed to save token:', error);
    console.error('Error details:', error.response?.data); // Debug log
    showToast('Failed to save token: ' + (error.message || error), 'danger');
  } finally {
    setSubmitting(false);
  }
};

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleManagePersona = async (token) => {
    try {
      const persona = await getPersonaByTokenId(token.id);
      setCurrentPersona({ token, persona });
      setPersonaFormData({
        persona: persona ? persona.persona : ''
      });
      setShowPersonaModal(true);
    } catch (error) {
      console.error('Failed to fetch persona:', error);
      showToast('Failed to load persona: ' + (error.message || error), 'danger');
    }
  };

  const handleSavePersona = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (currentPersona.persona) {
        // Update existing persona
        await updatePersona(currentPersona.persona.id, personaFormData);
        showToast('Persona updated successfully');
      } else {
        // Create new persona
        await createPersona({
          token_id: currentPersona.token.id,
          persona: personaFormData.persona
        });
        showToast('Persona created successfully');
      }
      setShowPersonaModal(false);
      fetchTokens(); // Refresh to update personas
    } catch (error) {
      console.error('Failed to save persona:', error);
      showToast('Failed to save persona: ' + (error.message || error), 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePersona = async () => {
    if (currentPersona.persona) {
      try {
        await deletePersona(currentPersona.persona.id);
        showToast('Persona deleted successfully');
        setShowPersonaModal(false);
        fetchTokens(); // Refresh to update personas
      } catch (error) {
        console.error('Failed to delete persona:', error);
        showToast('Failed to delete persona: ' + (error.message || error), 'danger');
      }
    }
  };

  // Schedule functions
  const handleScheduleClick = (token) => {
    setSchedulingToken(token);
    setScheduleFormData({
      is_scheduled: token.is_scheduled || false,
      scheduled_days: token.scheduled_days || [],
      scheduled_start_time: token.scheduled_start_time || '09:00',
      scheduled_end_time: token.scheduled_end_time || '17:00',
      scheduled_timezone: token.scheduled_timezone || 'America/New_York',
      response_delay_minutes: token.response_delay_minutes || 20
    });
    setShowScheduleModal(true);
  };

const formatTimeForAPI = (timeStr) => {
  if (!timeStr) return null;
  if (typeof timeStr === 'string') return timeStr;
  if (timeStr instanceof Date) {
    return timeStr.toTimeString().slice(0, 5);
  }
  // Handle time objects from Python backend
  if (timeStr && typeof timeStr === 'object' && timeStr.hour !== undefined && timeStr.minute !== undefined) {
    return `${timeStr.hour.toString().padStart(2, '0')}:${timeStr.minute.toString().padStart(2, '0')}`;
  }
  return timeStr;
};

// Update the handleSaveSchedule function
const handleSaveSchedule = async (e) => {
  e.preventDefault();
  setSubmitting(true);

  try {
    const formattedData = {
      ...scheduleFormData,
      scheduled_start_time: formatTimeForAPI(scheduleFormData.scheduled_start_time),
      scheduled_end_time: formatTimeForAPI(scheduleFormData.scheduled_end_time)
    };
    
    await toggleScheduledMode(schedulingToken.id, formattedData);
    showToast('Schedule updated successfully');
    setShowScheduleModal(false);
    fetchTokens(); // Refresh tokens
  } catch (error) {
    console.error('Failed to update schedule:', error);
    showToast('Failed to update schedule: ' + (error.message || error), 'danger');
  } finally {
    setSubmitting(false);
  }
};

const formatScheduleTime = (startTime, endTime) => {
  if (!startTime || !endTime) return 'Not set';
  
  const formatTime = (timeValue) => {
    let hours, minutes;
    
    if (typeof timeValue === 'string') {
      [hours, minutes] = timeValue.split(':');
    } else if (timeValue instanceof Date) {
      hours = timeValue.getHours();
      minutes = timeValue.getMinutes();
    } else if (timeValue && typeof timeValue === 'object') {
      // Handle datetime.time object from Python
      hours = timeValue.hour || 0;
      minutes = timeValue.minute || 0;
    } else {
      return 'Invalid time';
    }
    
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};
  const handleDayToggle = (day) => {
    setScheduleFormData(prev => {
      const days = [...prev.scheduled_days];
      if (days.includes(day)) {
        return { ...prev, scheduled_days: days.filter(d => d !== day) };
      } else {
        return { ...prev, scheduled_days: [...days, day] };
      }
    });
  };

  const formatScheduleDays = (days) => {
    if (!days || days.length === 0) return 'None';
    
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => dayNames[day]).join(', ');
  };



  // Bulk upload functions
  const handleShowUploadModal = () => {
    setShowUploadModal(true);
    setUploadResult(null);
    setUploadFile(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      showToast('Please select a file first', 'danger');
      return;
    }

    setUploading(true);
    try {
      let result;
      if (uploadType === 'csv') {
        result = await uploadTokensCSV(uploadFile);
      } else if (uploadType === 'json') {
        result = await uploadTokensJSON(uploadFile);
      } else if (uploadType === 'txt') {
        result = await uploadTokensTXT(uploadFile);
      }
      
      setUploadResult(result);
      if (result.errors && result.errors.length > 0) {
        showToast(`Upload completed with ${result.errors.length} errors`, 'warning');
      } else {
        showToast(`Successfully uploaded ${result.tokens_created || result.message} tokens`, 'success');
        setShowUploadModal(false);
        fetchTokens(); // Refresh the tokens list
      }
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Upload failed: ' + (error.message || error), 'danger');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    let template, fileType, fileName;
    
    if (uploadType === 'csv') {
      template = generateCSVTemplate('tokens');
      fileType = 'text/csv';
      fileName = 'tokens-template.csv';
    } else if (uploadType === 'json') {
      template = generateJSONTemplate('tokens');
      fileType = 'application/json';
      fileName = 'tokens-template.json';
    } else if (uploadType === 'txt') {
      template = generateTXTTemplate('tokens');
      fileType = 'text/plain';
      fileName = 'tokens-template.txt';
    }
    
    const blob = new Blob([template], { type: fileType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const maskToken = (tokenId) => {
    const token = decryptedTokens[tokenId];
    if (!token || token === 'N/A') return 'N/A';
    if (token.length <= 8) return token;
    return token.substring(0, 4) + '...' + token.substring(token.length - 4);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading tokens...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast 
          show={toastShow} 
          onClose={() => setToastShow(false)} 
          delay={5000} 
          autohide
          bg={toastVariant}
        >
          <Toast.Header className={`bg-${toastVariant} text-white justify-content-between`}>
            <div className="d-flex align-items-center">
              <CheckCircle size={18} className="me-2" />
              <strong className="me-auto">
                {toastVariant === 'success' ? 'Success' : 'Error'}
              </strong>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={() => setToastShow(false)}
              aria-label="Close"
            ></button>
          </Toast.Header>
          <Toast.Body className="bg-light">
            <div className="d-flex align-items-center">
              <div className="toast-message flex-grow-1">
                {toastMessage}
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Header */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div className="mb-3 mb-md-0">
          <h1 className="h2 fw-bold mb-1">Discord Tokens</h1>
          <p className="text-muted mb-0">
            Manage Discord bot tokens for reaction system
          </p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={handleShowUploadModal}>
            <Upload size={16} className="me-2" />
            <span className="d-none d-md-inline">Bulk Upload</span>
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <Plus size={16} className="me-2" />
            <span className="d-none d-md-inline">Add Token</span>
            <span className="d-md-none">Add</span>
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert variant="info" className="mb-4">
        <AlertTriangle size={20} className="me-2" />
        Discord tokens are used to perform reactions. Keep them secure and monitor their usage.
      </Alert>

      {/* Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <Key size={24} className="text-primary mb-2" />
              <h5 className="fw-bold mb-0">{tokens.length}</h5>
              <p className="text-muted mb-0 small">Total Tokens</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <CheckCircle size={24} className="text-success mb-2" />
              <h5 className="fw-bold mb-0">{tokens.filter(t => t.is_active).length}</h5>
              <p className="text-muted mb-0 small">Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-warning mb-2">⏰</div>
              <h5 className="fw-bold mb-0">{tokens.filter(t => t.status === 'cooldown').length}</h5>
              <p className="text-muted mb-0 small">Cooldown</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={6} lg={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center p-3">
              <div className="text-danger mb-2">❌</div>
              <h5 className="fw-bold mb-0">{tokens.filter(t => t.status === 'banned').length}</h5>
              <p className="text-muted mb-0 small">Banned</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-2">
            <Col xs={12} md={6}>
              <InputGroup size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search tokens..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={12} md={4}>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value='inactive'>Inactive</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={2}>
              <div className="text-muted small d-flex align-items-center h-100 justify-content-end justify-content-md-start">
                {filteredTokens.length} tokens
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tokens Table */}
      <Card className="shadow-sm border-0">
        <Card.Body className="p-0">
          {/* Desktop Table */}
          <div className="d-none d-md-block table-responsive">
            <Table className="mb-0" hover>
              <thead className="bg-light">
                <tr>
                  <th className="border-0 py-3 px-4">Label/Token</th>
                  <th className="border-0 py-3">Type</th>
                  <th className="border-0 py-3">Status</th>
                  <th className="border-0 py-3">Schedule</th>
                  <th className="border-0 py-3">Persona</th>
                  <th className="border-0 py-3">Usage</th>
                  <th className="border-0 py-3">Last Used</th>
                  <th className="border-0 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTokens.map((token) => {
                  const hasPersona = personas[token.id] !== undefined && personas[token.id] !== null;
                  return (
                    <tr key={token.id} style={{ cursor: 'pointer' }}>
                      <td className="px-4 py-3" onClick={() => toggleRowExpansion(token.id)}>
                        <div className="fw-medium">{token.label || 'Unlabeled'}</div>
                        <small className="text-muted font-monospace">
                          {maskToken(token.id)}
                        </small>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(token.id)}>
                        <div className="d-flex flex-column gap-1">
                          <Badge bg={getBotTypeBadge(token.bot_type)} className="text-capitalize">
                            {token.bot_type}
                          </Badge>
                          <Badge bg={getClientTypeBadge(token.client_type)} className="text-capitalize">
                            {token.client_type}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(token.id)}>
                        <div className="d-flex flex-column gap-1">
                          <Badge bg={token.is_active ? 'success' : 'secondary'}>
                            {token.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge bg={getStatusVariant(token.status)} className="text-capitalize">
                            {token.status}
                          </Badge>
                        </div>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(token.id)}>
                        {token.is_scheduled ? (
                          <Badge bg="info" className="d-flex align-items-center">
                            <Clock size={12} className="me-1" />
                            Scheduled
                          </Badge>
                        ) : (
                          <Badge bg="secondary">Always Active</Badge>
                        )}
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(token.id)}>
                        {token.bot_type === 'custom' ? (
                          <Button
                            variant={hasPersona ? "outline-success" : "outline-secondary"}
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleManagePersona(token);
                            }}
                            title={hasPersona ? "Edit Persona" : "Add Persona"}
                          >
                            <User size={14} />
                            {hasPersona ? " Configured" : " Add"}
                          </Button>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(token.id)}>
                        <div className="d-flex flex-column">
                          <small>Success: {token.success_count || 0}</small>
                          <small>Fails: {token.fail_count || 0}</small>
                          <small>Total: {token.usage_count || 0}</small>
                        </div>
                      </td>
                      <td className="py-3" onClick={() => toggleRowExpansion(token.id)}>
                        <small className="text-muted">{formatDate(token.last_used)}</small>
                      </td>
                      <td className="py-3">
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(token);
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="outline-info"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleScheduleClick(token);
                            }}
                          >
                            <Calendar size={14} />
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(token);
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="d-md-none">
            {filteredTokens.map((token) => {
              const hasPersona = personas[token.id] !== undefined && personas[token.id] !== null;
              const isExpanded = expandedRows.has(token.id);
              
              return (
                <Card key={token.id} className="mb-3 border-0 shadow-sm">
                  <Card.Body 
                    className="p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRowExpansion(token.id)}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <div className="fw-medium mb-1">{token.label || 'Unlabeled'}</div>
                        <small className="text-muted font-monospace">
                          {maskToken(token.id)}
                        </small>
                        <div className="mt-2 d-flex flex-wrap gap-1">
                          <Badge bg={getBotTypeBadge(token.bot_type)} className="text-capitalize">
                            {token.bot_type}
                          </Badge>
                          <Badge bg={getClientTypeBadge(token.client_type)} className="text-capitalize">
                            {token.client_type}
                          </Badge>
                          <Badge bg={token.is_active ? 'success' : 'secondary'}>
                            {token.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge bg={getStatusVariant(token.status)} className="text-capitalize">
                            {token.status}
                          </Badge>
                          {token.is_scheduled && (
                            <Badge bg="info" className="d-flex align-items-center">
                              <Clock size={10} className="me-1" />
                              Scheduled
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-top">
                        <Row className="g-3">
                          <Col xs={12}>
                            <div className="text-muted small">Schedule</div>
                            {token.is_scheduled ? (
                              <div className="small">
                                <div>Days: {formatScheduleDays(token.scheduled_days)}</div>
                                <div>Time: {formatScheduleTime(token.scheduled_start_time, token.scheduled_end_time)}</div>
                                <div>Timezone: {token.scheduled_timezone}</div>
                                <div>Response: {token.response_delay_minutes} min</div>
                              </div>
                            ) : (
                              <span className="text-muted">Always Active</span>
                            )}
                          </Col>
                          <Col xs={12}>
                            <div className="text-muted small">Persona</div>
                            {token.bot_type === 'custom' ? (
                              <Button
                                variant={hasPersona ? "outline-success" : "outline-secondary"}
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleManagePersona(token);
                                }}
                                title={hasPersona ? "Edit Persona" : "Add Persona"}
                                className="mt-1"
                              >
                                <User size={14} className="me-1" />
                                {hasPersona ? "Configured" : "Add Persona"}
                              </Button>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </Col>
                          <Col xs={6}>
                            <div className="text-muted small">Usage Stats</div>
                            <div className="small">
                              <div>Success: {token.success_count || 0}</div>
                              <div>Fails: {token.fail_count || 0}</div>
                              <div>Total: {token.usage_count || 0}</div>
                            </div>
                          </Col>
                          <Col xs={6}>
                            <div className="text-muted small">Last Used</div>
                            <div className="small">{formatDate(token.last_used)}</div>
                          </Col>
                          <Col xs={12}>
                            <div className="d-flex gap-2 justify-content-center mt-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(token);
                                }}
                              >
                                <Edit size={14} />
                              </Button>
                              <Button 
                                variant="outline-info"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleScheduleClick(token);
                                }}
                              >
                                <Calendar size={14} />
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(token);
                                }}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>

          {filteredTokens.length === 0 && (
            <div className="text-center py-5 text-muted">
              No tokens found. {searchTerm ? 'Try a different search term.' : 'Add your first token!'}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Token Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            {editingToken ? (
              <>
                <Edit size={20} className="me-2 text-primary" />
                Edit Token
              </>
            ) : (
              <>
                <Plus size={20} className="me-2 text-primary" />
                Add New Token
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Discord Token</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Discord bot token"
                    value={formData.token}
                    onChange={(e) => handleInputChange('token', e.target.value)}
                    className="py-2"
                    required
                  />
                  <Form.Text className="text-muted">
                    The Discord bot token to use for reactions
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Label</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Optional label for identification"
                    value={formData.label || ''}
                    onChange={(e) => handleInputChange('label', e.target.value)}
                    className="py-2"
                  />
                  <Form.Text className="text-muted">
                    Optional name to identify this token
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Bot Type</Form.Label>
                  <Form.Select
                    value={formData.bot_type}
                    onChange={(e) => handleInputChange('bot_type', e.target.value)}
                    className="py-2"
                  >
                    <option value="auto">Auto Bot</option>
                    <option value="custom">Custom Bot</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Type of Discord bot
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Client Type</Form.Label>
                  <Form.Select
                    value={formData.client_type}
                    onChange={(e) => handleInputChange('client_type', e.target.value)}
                    className="py-2"
                  >
                    <option value="web">Web</option>
                    <option value="desktop">Desktop</option>
                    <option value="mobile">Mobile</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Discord client type to simulate
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Status</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="py-2"
                  >
                    <option value="healthy">Healthy</option>
                    <option value="cooldown">Cooldown</option>
                    <option value="banned">Banned</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Current status of the token
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Usage Threshold</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.usage_threshold}
                    onChange={(e) => handleInputChange('usage_threshold', parseInt(e.target.value))}
                    className="py-2"
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Max usage before cooldown
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-medium">Cooldown Duration (min)</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.cooldown_duration}
                    onChange={(e) => handleInputChange('cooldown_duration', parseInt(e.target.value))}
                    className="py-2"
                    min="1"
                  />
                  <Form.Text className="text-muted">
                    Cooldown time after threshold
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Check
                  type="switch"
                  id="active-switch"
                  label="Active Token"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className="mb-3"
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingToken ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingToken ? 'Update Token' : 'Create Token'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Schedule Modal */}
      <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            <Clock size={20} className="me-2 text-info" />
            Configure Schedule for Token
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveSchedule}>
          <Modal.Body className="pt-0">
            <Row className="g-3">
              <Col xs={12}>
                <Form.Check
                  type="switch"
                  id="schedule-switch"
                  label="Enable Scheduled Mode"
                  checked={scheduleFormData.is_scheduled}
                  onChange={(e) => setScheduleFormData(prev => ({
                    ...prev,
                    is_scheduled: e.target.checked
                  }))}
                  className="mb-3"
                />
              </Col>

              {scheduleFormData.is_scheduled && (
                <>
                  <Col xs={12}>
                    <Form.Label className="fw-medium">Active Days</Form.Label>
                    <div className="d-flex flex-wrap gap-2">
                      {daysOfWeek.map(day => (
                        <Button
                          key={day.value}
                          variant={scheduleFormData.scheduled_days.includes(day.value) ? 'primary' : 'outline-primary'}
                          size="sm"
                          type="button"
                          onClick={() => handleDayToggle(day.value)}
                        >
                          {day.label}
                        </Button>
                      ))}
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Start Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleFormData.scheduled_start_time}
                        onChange={(e) => setScheduleFormData(prev => ({
                          ...prev,
                          scheduled_start_time: e.target.value
                        }))}
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">End Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleFormData.scheduled_end_time}
                        onChange={(e) => setScheduleFormData(prev => ({
                          ...prev,
                          scheduled_end_time: e.target.value
                        }))}
                        className="py-2"
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Timezone</Form.Label>
                      <Form.Select
                        value={scheduleFormData.scheduled_timezone}
                        onChange={(e) => setScheduleFormData(prev => ({
                          ...prev,
                          scheduled_timezone: e.target.value
                        }))}
                        className="py-2"
                      >
                        {timezones.map(tz => (
                          <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-medium">Response Delay (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        value={scheduleFormData.response_delay_minutes}
                        onChange={(e) => setScheduleFormData(prev => ({
                          ...prev,
                          response_delay_minutes: parseInt(e.target.value)
                        }))}
                        className="py-2"
                        min="1"
                      />
                      <Form.Text className="text-muted">
                        Delay between messages when scheduled
                      </Form.Text>
                    </Form.Group>
                  </Col>

                  <Col xs={12}>
                    <Alert variant="info" className="small">
                      <strong>Schedule Summary:</strong><br />
                      Active on: {formatScheduleDays(scheduleFormData.scheduled_days)}<br />
                      Time: {formatScheduleTime(scheduleFormData.scheduled_start_time, scheduleFormData.scheduled_end_time)}<br />
                      Timezone: {timezones.find(tz => tz.value === scheduleFormData.scheduled_timezone)?.label}<br />
                      Response Delay: {scheduleFormData.response_delay_minutes} minutes per message
                    </Alert>
                  </Col>
                </>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            <Button variant="outline-secondary" onClick={() => setShowScheduleModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Schedule'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5 fw-bold text-danger">
            <Trash2 size={20} className="me-2" />
            Delete Token
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this token?</p>
          {tokenToDelete && (
            <div className="bg-light p-3 rounded">
              <strong>Token:</strong> {maskToken(tokenToDelete.id)}<br />
              {tokenToDelete.label && <><strong>Label:</strong> {tokenToDelete.label}</>}
            </div>
          )}
          <p className="text-danger mt-3 mb-0">
            <AlertTriangle size={16} className="me-1" />
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete Token
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Persona Modal */}
      <Modal show={showPersonaModal} onHide={() => setShowPersonaModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            <User size={20} className="me-2 text-success" />
            {currentPersona?.persona ? 'Edit Persona' : 'Add Persona'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSavePersona}>
          <Modal.Body className="pt-0">
            {currentPersona && (
              <div className="mb-3">
                <small className="text-muted">Token: {maskToken(currentPersona.token.id)}</small>
                {currentPersona.token.label && (
                  <small className="text-muted ms-2">({currentPersona.token.label})</small>
                )}
              </div>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium">Persona Configuration</Form.Label>
              <Form.Control
                as="textarea"
                rows={8}
                placeholder="Enter persona configuration (JSON format)"
                value={personaFormData.persona}
                onChange={(e) => setPersonaFormData({ persona: e.target.value })}
                className="font-monospace"
              />
              <Form.Text className="text-muted">
                Define the bot's personality, behavior, and response patterns in JSON format
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-top-0 pt-0">
            {currentPersona?.persona && (
              <Button 
                variant="outline-danger" 
                onClick={handleDeletePersona}
                className="me-auto"
              >
                <Trash2 size={14} className="me-1" />
                Delete Persona
              </Button>
            )}
            <Button variant="outline-secondary" onClick={() => setShowPersonaModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Persona'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            <Upload size={20} className="me-2 text-primary" />
            Bulk Upload Tokens
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Tabs
            activeKey={uploadType}
            onSelect={(k) => setUploadType(k)}
            className="mb-3"
          >
            <Tab eventKey="csv" title="CSV">
              <div className="mb-3">
                <p>Upload a CSV file with token data. The file should include the following columns:</p>
                <ul className="small">
                  <li><code>token</code> (required) - The Discord bot token</li>
                  <li><code>label</code> (optional) - A label for the token</li>
                  <li><code>bot_type</code> (optional) - "auto" or "custom"</li>
                  <li><code>client_type</code> (optional) - "web", "desktop", or "mobile"</li>
                  <li><code>is_active</code> (optional) - "true" or "false"</li>
                </ul>
              </div>
            </Tab>
            <Tab eventKey="json" title="JSON">
              <div className="mb-3">
                <p>Upload a JSON file with token data. The file should be an array of objects with these properties:</p>
                <pre className="bg-light p-2 small">
{`[
  {
    "token": "discord_token_here",
    "label": "Optional label",
    "bot_type": "auto",
    "client_type": "web",
    "is_active": true
  }
]`}
                </pre>
              </div>
            </Tab>
            <Tab eventKey="txt" title="Text">
              <div className="mb-3">
                <p>Upload a text file with one token per line. Optionally, you can include a label after the token separated by a comma:</p>
                <pre className="bg-light p-2 small">
{`token1,Label for token 1
token2,Label for token 2
token3`}
                </pre>
              </div>
            </Tab>
          </Tabs>

          <div className="mb-3">
            <Form.Label className="fw-medium">Select File</Form.Label>
            <Form.Control
              type="file"
              accept={uploadType === 'csv' ? '.csv' : uploadType === 'json' ? '.json' : '.txt'}
              onChange={handleFileSelect}
              ref={fileInputRef}
            />
          </div>

          {uploadResult && uploadResult.errors && uploadResult.errors.length > 0 && (
            <Alert variant="warning" className="small">
              <AlertTriangle size={16} className="me-2" />
              <strong>Upload completed with {uploadResult.errors.length} errors:</strong>
              <ul className="mb-0 mt-2">
                {uploadResult.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {uploadResult && uploadResult.tokens_created > 0 && (
            <Alert variant="success" className="small">
              <CheckCircle size={16} className="me-2" />
              Successfully created {uploadResult.tokens_created} tokens.
            </Alert>
          )}

          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={downloadTemplate} size="sm">
              <Download size={14} className="me-1" />
              Download Template
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <Button variant="outline-secondary" onClick={() => setShowUploadModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpload} 
            disabled={!uploadFile || uploading}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Uploading...
              </>
            ) : (
              'Upload Tokens'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Tokens;